import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { StatusBadge, PriorityBadge } from '../components/custom/StatusBadge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  ArrowLeft, 
  Send, 
  Paperclip, 
  Download, 
  Clock, 
  User, 
  Tag,
  Eye,
  EyeOff,
  Upload,
  Loader2
} from 'lucide-react';
import { cn } from '../lib/utils';

export default function TicketDetailPage() {
  const { ticketId } = useParams();
  const { api, user, isStaff } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    fetchTicket();
    if (isStaff) {
      fetchUsers();
    }
  }, [ticketId]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchTicket = async () => {
    try {
      const response = await api.get(`/tickets/${ticketId}`);
      setTicket(response.data);
    } catch (error) {
      console.error('Failed to fetch ticket:', error);
      toast.error('Ticket non trouvé');
      navigate(isStaff ? '/backoffice/tickets' : '/portal');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.filter(u => ['agent', 'moderator', 'admin'].includes(u.role)));
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleDownloadAttachment = async (file) => {
    try {
      const response = await api.get(`/attachments/${file.id}`, {
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: file.content_type });
      const url = window.URL.createObjectURL(blob);
      
      // For images and PDFs, open in new tab; otherwise download
      const viewableTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf'];
      
      if (viewableTypes.includes(file.content_type)) {
        window.open(url, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = file.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      // Clean up
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du téléchargement du fichier');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await api.post(`/tickets/${ticketId}/messages`, {
        content: newMessage,
        is_internal: isInternal
      });
      setNewMessage('');
      setIsInternal(false);
      fetchTicket();
      toast.success('Message envoyé');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await api.put(`/tickets/${ticketId}`, { status });
      fetchTicket();
      toast.success('Statut mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleAssign = async (assignedTo) => {
    try {
      await api.put(`/tickets/${ticketId}`, { assigned_to: assignedTo });
      fetchTicket();
      toast.success('Ticket assigné');
    } catch (error) {
      toast.error('Erreur lors de l\'assignation');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 Mo)');
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/tickets/${ticketId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchTicket();
      toast.success('Fichier uploadé');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadingFile(false);
    }
  };

  const categoryNames = {
    facturation: 'Facturation',
    'compta-gestion': 'Compta/Gestion',
    juridique: 'Juridique',
    administratif: 'Administratif',
    conseil: 'Conseil',
    autres: 'Autres'
  };

  if (loading) {
    return (
      <div className={cn("p-6", !isStaff && "max-w-5xl mx-auto")}>
        <Skeleton className="h-8 w-64 mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Skeleton className="h-48" />
            <Skeleton className="h-96" />
          </div>
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!ticket) return null;

  return (
    <div data-testid="ticket-detail-page" className={cn("p-6", !isStaff && "max-w-5xl mx-auto")}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isStaff ? '/backoffice/tickets' : '/portal')}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <span className="mono text-sm text-slate-500 dark:text-zinc-500">
              {ticket.ticket_number}
            </span>
            <StatusBadge status={ticket.status} />
            <PriorityBadge priority={ticket.priority} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {ticket.title}
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card className="border-slate-200 dark:border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 dark:text-zinc-300 whitespace-pre-wrap">
                {ticket.description}
              </p>
            </CardContent>
          </Card>

          {/* Attachments */}
          {ticket.attachments?.length > 0 && (
            <Card className="border-slate-200 dark:border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Paperclip className="w-5 h-5" />
                  Pièces jointes ({ticket.attachments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {ticket.attachments.map((file) => (
                    <button
                      key={file.id}
                      onClick={() => handleDownloadAttachment(file)}
                      className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-zinc-900 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors text-left"
                    >
                      <Download className="w-4 h-4 text-slate-500" />
                      <span className="text-sm text-slate-700 dark:text-zinc-300 truncate flex-1">
                        {file.filename}
                      </span>
                      <span className="text-xs text-slate-500">
                        {(file.size / 1024).toFixed(0)} Ko
                      </span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Messages */}
          <Card className="border-slate-200 dark:border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Discussion</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {ticket.messages?.length === 0 ? (
                <p className="text-center text-slate-500 dark:text-zinc-400 py-8">
                  Aucun message pour le moment
                </p>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                  {ticket.messages?.map((message) => (
                    <div
                      key={message.id}
                      className={cn(
                        "flex gap-3",
                        message.is_internal && "bg-amber-50 dark:bg-amber-900/10 p-3 rounded-lg border border-amber-200 dark:border-amber-800"
                      )}
                    >
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className={cn(
                          "text-xs",
                          message.author_role === 'client' 
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                        )}>
                          {message.author_name?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm text-slate-900 dark:text-white">
                            {message.author_name}
                          </span>
                          <span className="text-xs text-slate-500 dark:text-zinc-500">
                            {formatDistanceToNow(new Date(message.created_at), { addSuffix: true, locale: fr })}
                          </span>
                          {message.is_internal && (
                            <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                              <EyeOff className="w-3 h-3" />
                              Note interne
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-700 dark:text-zinc-300 whitespace-pre-wrap">
                          {message.content}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}

              {/* New Message Form */}
              <form onSubmit={handleSendMessage} className="space-y-3 pt-4 border-t border-slate-200 dark:border-zinc-800">
                <Textarea
                  placeholder="Écrivez votre message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  rows={3}
                  data-testid="message-input"
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {isStaff && (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="internal"
                          checked={isInternal}
                          onCheckedChange={setIsInternal}
                        />
                        <Label htmlFor="internal" className="text-sm text-slate-600 dark:text-zinc-400 cursor-pointer">
                          Note interne
                        </Label>
                      </div>
                    )}
                    <div className="relative">
                      <input
                        type="file"
                        id="message-file"
                        className="hidden"
                        onChange={handleFileUpload}
                      />
                      <label
                        htmlFor="message-file"
                        className="flex items-center gap-1 text-sm text-slate-500 hover:text-emerald-600 cursor-pointer transition-colors"
                      >
                        {uploadingFile ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                        Joindre
                      </label>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    data-testid="send-message-btn"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    disabled={sending || !newMessage.trim()}
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Envoyer
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Ticket Info */}
          <Card className="border-slate-200 dark:border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Informations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-slate-500 dark:text-zinc-500 mb-1">Catégorie</p>
                <p className="text-sm font-medium text-slate-900 dark:text-white">
                  {categoryNames[ticket.category] || ticket.category}
                </p>
              </div>
              
              <div>
                <p className="text-xs text-slate-500 dark:text-zinc-500 mb-1">Créé par</p>
                <p className="text-sm font-medium text-slate-900 dark:text-white">
                  {ticket.created_by_name}
                </p>
              </div>
              
              <div>
                <p className="text-xs text-slate-500 dark:text-zinc-500 mb-1">Date de création</p>
                <p className="text-sm font-medium text-slate-900 dark:text-white">
                  {format(new Date(ticket.created_at), 'PPP à HH:mm', { locale: fr })}
                </p>
              </div>

              <div>
                <p className="text-xs text-slate-500 dark:text-zinc-500 mb-1">Pris en charge par</p>
                {ticket.assigned_to_name ? (
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                      <User className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      {ticket.assigned_to_name}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-zinc-400 italic">
                    En attente d'attribution
                  </p>
                )}
              </div>

              {ticket.deadline && (
                <div>
                  <p className="text-xs text-slate-500 dark:text-zinc-500 mb-1">Date limite</p>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    {format(new Date(ticket.deadline), 'PPP', { locale: fr })}
                  </p>
                </div>
              )}

              {ticket.resolved_at && (
                <div>
                  <p className="text-xs text-slate-500 dark:text-zinc-500 mb-1">Résolu le</p>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    {format(new Date(ticket.resolved_at), 'PPP à HH:mm', { locale: fr })}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Staff Actions */}
          {isStaff && (
            <Card className="border-slate-200 dark:border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg">Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-500 dark:text-zinc-500">Statut</Label>
                  <Select
                    value={ticket.status}
                    onValueChange={handleStatusChange}
                  >
                    <SelectTrigger className="mt-1" data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="open">En cours</SelectItem>
                      <SelectItem value="blocked">Bloqué</SelectItem>
                      <SelectItem value="resolved">Résolu</SelectItem>
                      <SelectItem value="closed">Fermé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-slate-500 dark:text-zinc-500">Assigné à</Label>
                  <Select
                    value={ticket.assigned_to || ''}
                    onValueChange={handleAssign}
                  >
                    <SelectTrigger className="mt-1" data-testid="assign-select">
                      <SelectValue placeholder="Non assigné" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {ticket.assigned_to_name && (
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                    <User className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-sm text-emerald-700 dark:text-emerald-300">
                      {ticket.assigned_to_name}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tags */}
          {ticket.tags?.length > 0 && (
            <Card className="border-slate-200 dark:border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  Tags
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {ticket.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
